import React from 'react';
import { Star, ExternalLink, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-black text-white">
            Find Us on{' '}
            <span className="inline-block">
              <span style={{ color: '#4285F4' }}>G</span>
              <span style={{ color: '#EA4335' }}>o</span>
              <span style={{ color: '#FBBC04' }}>o</span>
              <span style={{ color: '#4285F4' }}>g</span>
              <span style={{ color: '#34A853' }}>l</span>
              <span style={{ color: '#EA4335' }}>e</span>
            </span>
          </h2>
          <p className="text-2xl text-yellow-400 font-bold">— Read Our Real Reviews —</p>
        </div>

        {/* Google Reviews Section */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="bg-gradient-to-br from-blue-600/10 to-blue-800/10 backdrop-blur-sm border border-blue-500/30 rounded-3xl p-8 md:p-12">
            {/* Google Logo and Rating */}
            <div className="flex flex-col items-center justify-center mb-6 space-y-4">
              <svg className="w-16 h-16" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
              </svg>
              <div className="text-center">
                <h3 className="text-2xl md:text-3xl font-black text-white mb-2">Google Reviews</h3>
                <div className="flex items-center justify-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={24} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </div>
            </div>

            {/* Description */}
            <p className="text-center text-gray-300 mb-8 text-lg">
              See what our customers are saying on Google. Over <span className="text-yellow-400 font-bold">10,000 verified reviews</span> from real jumpers who experienced the thrill.
            </p>

            {/* CTA Button */}
            <div className="flex justify-center">
              <a
                href="https://www.google.com/search?sca_esv=e4bf3edc85bebf14&rlz=1C1ONGR_en-GBIN1070IN1070&sxsrf=AE3TifPzbwKyDbD21j-CadxpVsu6jgaWMg:1763844371515&q=Indya+Bungy+Goa&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E-lMAQYt7YrNMoCQkP6wt0SrPwNKMyM0l27SXtJ8PVsmZVEOiLFDZDeuU4KwjfOEmG0URJRFCtUYyr5Ia0wFqPsVrtpx&sa=X&ved=2ahUKEwix4c-p0IaRAxWY-DgGHXwSHsEQmJ0LegQIIBAA&biw=1422&bih=650&dpr=1.35"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold px-8 py-4 rounded-full text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-blue-500/50"
              >
                <span>Read All Google Reviews</span>
                <ExternalLink size={20} />
              </a>
            </div>

            {/* Trust Badges */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-sm text-gray-400">
              <div className="flex items-center space-x-2">
                <Star size={16} className="text-yellow-400 fill-yellow-400" />
                <span>Trusted by thousands</span>
              </div>
              <div className="w-px h-4 bg-gray-600"></div>
              <div className="flex items-center space-x-2">
                <span>✅</span>
                <span>Certified for safety</span>
              </div>
              <div className="w-px h-4 bg-gray-600"></div>
              <div className="flex items-center space-x-2">
                <span>🏆</span>
                <span>Committed to excellence</span>
              </div>
            </div>
          </div>
        </div>

        {/* CTA - Redirects to /locations */}
        <div className="text-center">
          <button 
            onClick={() => navigate('/locations')}
            className="bg-yellow-400 hover:bg-yellow-300 text-black px-12 py-4 rounded-full font-black text-xl transform hover:scale-105 transition-all duration-300 shadow-lg shadow-yellow-400/50"
          >
            BOOK YOUR JUMP NOW
          </button>
        </div>

        {/* Social Media */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-6 font-medium">Follow Us</p>
          <div className="flex justify-center flex-wrap gap-4">
            <a
              href="https://www.instagram.com/indyabungy/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-white/5 hover:bg-pink-500 flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              aria-label="Instagram"
            >
              <Instagram className="text-white" size={20} />
            </a>
            <a
              href="https://www.youtube.com/@INDYABUNGY"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-white/5 hover:bg-red-500 flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              aria-label="YouTube"
            >
              <Youtube className="text-white" size={20} />
            </a>
            <a
              href="https://www.snapchat.com/add/indya.bungy?share_id=pqLCTVesrII&locale=en-IN"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              aria-label="Snapchat"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_extreme-homepage/artifacts/ssgswl2e_vecteezy_snapchat-circle-logo-icon-snapchat-app-transparent_65386652.png"
                alt="Snapchat"
                className="w-7 h-7 object-contain"
              />
            </a>
            <a
              href="https://www.linkedin.com/in/siddharth-pareek-872070153/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-white/5 hover:bg-blue-600 flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              aria-label="LinkedIn"
            >
              <Linkedin className="text-white" size={20} />
            </a>
            <a
              href="https://www.facebook.com/profile.php?id=61564503379743"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 rounded-full bg-white/5 hover:bg-blue-500 flex items-center justify-center transition-all duration-300 transform hover:scale-110"
              aria-label="Facebook"
            >
              <Facebook className="text-white" size={20} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;